import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CloseButton, Col, Container, Form, Row, Spinner, Stack, Toast, ToastContainer } from 'react-bootstrap'
import Datetime from 'react-datetime'
import 'react-datetime/css/react-datetime.css'

import './ImportSession.scss'
import moment, { Moment } from 'moment/moment'
import { Allocation, FileDownloadLocationResponse, FileKind, FileModel, RoomInfo, RoomSession, User } from '@zocks-communications/client-sdk-js'
import { nanoid } from 'nanoid'

import { useNavigate } from 'react-router-dom'
import { chartColors, getUserDisplayName } from './common/aifeatures/utils'
import classNames from 'classnames'
import { TrackEvent, useTracking } from './common/tracking'
import { useAPIClient } from './APIClientProvider'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { Button } from '../../../components/Button/Button'
import { TagElement } from '../../../components/Tag/Tag'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { useGetMeQuery, useGetUsersQuery } from '../../../store/user/user'
import { useOutsideClick } from '../../../common/useOutsideClick'
import { AddContactModal } from '../../../views/Contacts/AddContactModal'
import { User as UserModel } from '../../../models/User'
import { useImportSessionMutation, useTriggerEventMutation } from '../../../store/media/media'
import { UserTagDropdownItemOption } from '../../../components/SelectDropdown/UserTagDropdownItemOption'

import { ReactComponent as PhoneLineLogo } from '../../../assets/images/phone-line.svg'
import ZocksBackgroundLogo from '../../../assets/images/zocks-background-logo.svg?url'
import { MeetingProviderAccountIntegrationConfig } from '../../../models/Account'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { createPortal } from 'react-dom'
import { HeaderContext } from '../../../views/LayoutWithLeftSidebar/HeaderContext'
import { isEnhancedAllocatedSessionTranscription } from './utils'
import { LANGUAGE_OPTIONS_WITH_AUTO_DETECT } from '../../../common/languageUtils'
import { LanguageConfigSource, MeetingLanguage } from '../../../models/Room'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { isApiErrors } from '../../../models/ApiErrors'
import { useMultiLanguageEnabled } from '../../../common/useMultiLanguageEnabled'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

const _250_MB_IN_BYTES = 250 * 1024 * 1024
const _60_MB_IN_BYTES = 60 * 1024 * 1024
const _MAX_IMPORTED_SESSION_DURATION_IN_SECONDS = 4 * 60 * 60

interface UserInput {
  type: 'anonymous' | 'managed'
  name: string
  id: string
}

export const ImportSessionModal: React.FC<{
  onImportFinished?: (roomId: string, sessionId: string) => void
}> = ({ onImportFinished }) => {
  const navigate = useNavigate()

  const onImportFinishedCallback = async (roomId: string, sessionId: string) => {
    onImportFinished?.(roomId, sessionId)
    navigate(`/workspace/past-meetings/room/${roomId}/session/${sessionId}`)
  }

  const { portalRef } = useContext(HeaderContext)

  return (
    <div className='w-100'>
      {portalRef && portalRef.current ? createPortal(<ImportSessionHeader />, portalRef.current) : null}
      <ImportSessionForm onImportFinished={onImportFinishedCallback} />
    </div>
  )
}

export const ImportSessionHeader = () => {
  const navigate = useNavigate()

  const handleBackButtonClicked = () => {
    navigate(
      {
        pathname: '/workspace/past-meetings',
      },
      { replace: true },
    )
  }

  return (
    <div className='d-flex flex-column'>
      <div className='d-flex zocks-pl-2.0 justify-content-between align-items-center'>
        <div className='d-flex align-items-center zocks-gap-2'>
          <div className='d-none align-items-center d-sm-flex zocks-gap-1.25'>
            <Button variant='link' className='back-button' onClick={handleBackButtonClicked}>
              <i className='bi bi-chevron-left' />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const ImportSessionForm: React.FC<{
  onImportFinished: (roomId: string, sessionId: string, fileId: string) => void
}> = ({ onImportFinished }) => {
  const { mediaClient } = useAPIClient()
  const { tracked } = useTracking()

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [importStatus, setImportStatus] = useState<'idle' | 'loading' | 'failed' | 'succeed'>('idle')
  const [fileStatus, setFileStatus] = useState<'idle' | 'loading' | 'failed' | 'succeed'>('idle')
  const [fileId, setFileId] = useState<null | string>(null)
  const [fileDuration, setFileDuration] = useState<number | null>(null)

  const now = moment()
  const [startDate, setStartDate] = useState<string | Moment>(
    now.set({
      hour: Math.min(now.hour(), 9),
      minute: 0,
      second: 0,
      millisecond: 0,
    }),
  )
  const [validStartDate, setValidStartDate] = useState<boolean>(true)
  const [endDate, setEndDate] = useState<string | Moment>('')
  const [rooms] = useState<RoomInfo[]>([])
  const [roomIdInput, setRoomIdInput] = useState<string>('')
  const [selectedUsers, setSelectedUsers] = useState<UserInput[]>([])
  const [meetingTitle, setMeetingTitle] = useState<string>('')
  const inputElement = useRef<HTMLInputElement>(null)
  const [cursorPosition, setCursorPosition] = useState(0)
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const { data: currentUser } = useGetMeQuery({ accountId })
  const [sharedPhoneLineEnabled, setSharedPhoneLineEnabled] = useState<boolean>(false)
  const [importSession] = useImportSessionMutation()
  const { multiLanguageEnabled } = useMultiLanguageEnabled()
  const { hasQuota } = useZocksQuota()
  const hasMultiLanguageSupportQuota = hasQuota('ai.multiLanguageSupport.value')

  // https://github.com/arqex/react-datetime/issues/755
  // TODO: we should looking for a better datetime component, because this is a little buggy, and uses moment
  useEffect(() => {
    if (inputElement.current && inputElement.current === document.activeElement) {
      inputElement.current.setSelectionRange(cursorPosition, cursorPosition)
    }
  }, [startDate, cursorPosition])
  const onStartDateChanged = (newDate: Moment | string) => {
    setValidStartDate(true)
    if (moment(newDate).isValid()) {
      setStartDate(newDate)
      adjustEndDate(newDate, fileDuration)
    }
  }

  const onInput = () => inputElement.current && setCursorPosition(inputElement.current.selectionStart || 1)

  const adjustEndDate = (startDate: Moment | string, fileDuration: number | null) => {
    if (startDate && fileDuration) {
      if (fileDuration > _MAX_IMPORTED_SESSION_DURATION_IN_SECONDS) {
        setEndDate(moment(startDate).add(_MAX_IMPORTED_SESSION_DURATION_IN_SECONDS, 's'))
      } else {
        setEndDate(moment(startDate).add(fileDuration, 's'))
      }
    }
  }

  //TODO: later we might add back with roomnames
  // useEffect(() => {
  //   setRoomsLoading(true)
  //   mediaClient
  //     .listRooms()
  //     .then((roomInfos) => {
  //       setRooms(roomInfos)
  //     })
  //     .finally(() => {
  //       setRoomsLoading(false)
  //     })
  // }, [mediaClient])

  const onEndDateChanged = (newDate: Moment | string) => {
    setEndDate(newDate)
  }

  const validateStartDate = (startDate: Moment) => {
    const now = moment()
    return now.isSameOrAfter(startDate)
  }

  const validateEndDate = (endDate: Moment) => {
    if (startDate) {
      return endDate.isSameOrAfter(startDate)
    }
    return true
  }

  const resetFile = () => {
    setFileStatus('idle')
    setFileDuration(null)
    setFileId(null)
  }
  const [selectedLanguage, setSelectedLanguage] = useState<MeetingLanguage>(MeetingLanguage.AUTO_DETECT)

  const handleOptionSelected = useCallback((option: SelectOption) => {
    setSelectedLanguage(option.id as MeetingLanguage)
  }, [])

  const fileUpload = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files ? evt.target.files[0] : null
    if (!file) {
      resetFile()
      return
    }
    if (file.size > _250_MB_IN_BYTES) {
      removeFile()
      setErrorMessage('File is too big. Maximum allowed size: 250MB')
      return
    }
    setFileStatus('loading')
    if (!meetingTitle) {
      setMeetingTitle(file.name.substring(0, file.name.lastIndexOf('.')) || file.name)
    }
    try {
      const {
        fileUploadUrl,
        fileUploadParameters,
        fileInfo: { fileId },
      } = await mediaClient.uploadGlobalFileRequest({
        contentType: file.type,
        fileName: file.name,
        fileSize: file.size,
        kind: FileKind.RECORDING,
      })
      setFileId(fileId)
      await mediaClient.uploadFile(fileUploadUrl, fileUploadParameters, file)
      if (file.size > _60_MB_IN_BYTES) {
        setFileDuration(60)
        adjustEndDate(startDate, 60 * 60)
      } else {
        await determineMeetingLength(file)
      }
    } catch (e) {
      setFileStatus('failed')
    }
    setFileStatus('succeed')
  }

  const determineMeetingLength = async (file: File) => {
    const audioContext = new window.AudioContext()
    const arrayBuffer = await file.arrayBuffer()
    await audioContext
      .decodeAudioData(arrayBuffer, (buffer) => {
        setFileDuration(buffer.duration)
        adjustEndDate(startDate, buffer.duration)
      })
      .catch((e) => {
        setFileDuration(30)
        adjustEndDate(startDate, 30 * 60)
        console.error(e)
      })
  }

  const validateForm = () => {
    try {
      const selectedStartDate = startDate as Moment
      selectedStartDate.toISOString()
      if (selectedStartDate.isSameOrAfter(moment())) {
        setValidStartDate(false)
        return false
      }
    } catch (e) {
      setValidStartDate(false)
      return false
    }
    return true
  }

  const _onImport = async () => {
    if (!validateForm()) {
      return
    }
    adjustEndDate(startDate, fileDuration)
    if (fileId && startDate && endDate) {
      setImportStatus('loading')
      await importSession({
        accountId,
        body: {
          users: selectedUsers.map((user) => {
            return {
              type: user.type,
              ...(user.type === 'anonymous' && { name: user.name }),
              ...(user.type === 'managed' && { id: user.id }),
            }
          }),
          startedAt: (startDate as Moment).toISOString(),
          finishedAt: (endDate as Moment).toISOString(),
          fileId: fileId,
          ...(roomIdInput && { roomId: roomIdInput }),
          ...(meetingTitle && { metadata: { title: meetingTitle } }),
          ...(multiLanguageEnabled &&
            hasMultiLanguageSupportQuota && {
              config: {
                language: { language: selectedLanguage, source: LanguageConfigSource.MANUAL, updatedAt: new Date().toISOString() },
                isRecurring: false,
                autoJoin: false,
                autoJoinOverride: false,
              },
            }),
        },
      })
        .unwrap()
        .then((response) => {
          onImportFinished(response.roomId, response.id, fileId)
          setImportStatus('succeed')
        })
        .catch((e: unknown) => {
          setImportStatus('failed')
          if (isApiErrors(e)) {
            setErrorMessage(e.errors.map((err) => err.message).join(', '))
          } else {
            setErrorMessage(JSON.stringify(e))
          }
        })
    }
  }

  const onImport = tracked(_onImport, {
    eventName: TrackEvent.RECORDING_UPLOADED,
    eventDetails: {
      meetingTitle,
      startDate: startDate && moment.isMoment(startDate) ? (startDate as Moment).toISOString() : '',
      endDate: endDate && moment.isMoment(endDate) ? (endDate as Moment).toISOString() : '',
      fileId,
      users: selectedUsers,
      ...(multiLanguageEnabled && hasMultiLanguageSupportQuota && { language: selectedLanguage }),
    },
  })

  const fileRef = useRef<HTMLInputElement>(null)
  const removeFile = () => {
    if (fileRef && fileRef.current && fileRef.current.value) {
      resetFile()
      fileRef.current.value = ''
    }
  }

  const errorToast = (
    <ToastContainer className='p-3' position={'top-end'} style={{ zIndex: 1 }}>
      <Toast show={!!errorMessage} onClose={() => setErrorMessage(null)} bg='danger'>
        <Toast.Header className='ondemand-error-toast'>
          <span>Save Error</span>
        </Toast.Header>
        <Toast.Body>{'Error during importing the media: ' + errorMessage}</Toast.Body>
      </Toast>
    </ToastContainer>
  )

  const onStartDateOpen = () => {
    if (!startDate) {
      const initialDate = moment()
      initialDate.set({ hour: Math.min(initialDate.hour(), 9), minute: 0, second: 0, millisecond: 0 })
      setStartDate(initialDate)
    }
  }

  const handleSetupSharedPhoneLine = () => {
    navigate(`/workspace/setup/settings/`)
  }

  useEffect(() => {
    if (!account?.data?.effectiveConfig?.integrations) {
      return
    }
    const phoneIntegration = account.data.effectiveConfig.integrations.find(
      (integration) => integration.type === 'meeting_provider' && integration.serviceId === 'phone',
    ) as MeetingProviderAccountIntegrationConfig
    if (!phoneIntegration) {
      return
    }
    setSharedPhoneLineEnabled(phoneIntegration.sharedPhoneLineEnabled)
  }, [account])

  return (
    <div className='upload-meeting-page-container px-4'>
      <div className='upload-meeting-container'>
        <div className='upload-container'>
          <h4 className='zocks-pb-2'>Upload Meeting Audio</h4>
          <div>
            {errorToast}
            <div hidden={true}>
              Meeting id:
              <div>
                <input className='form-control' type='text' list='rooms' value={roomIdInput} onChange={(e) => setRoomIdInput(e.currentTarget.value)} />
                <datalist id='rooms'>
                  {rooms.map((room) => {
                    return (
                      <option key={room.id} value={room.id}>
                        {room.id}
                      </option>
                    )
                  })}
                </datalist>
              </div>
            </div>
          </div>
          <div className='pb-4'>
            <label className='label-medium'>Meeting title</label>
            <input
              className='form-control'
              type='text'
              list='rooms'
              value={meetingTitle}
              placeholder='Darlene & Robert Meeting'
              onChange={(e) => setMeetingTitle(e.currentTarget.value)}
            />
          </div>
          <div className='pb-4'>
            <label className='label-medium'>Date and Time of Meeting</label>
            <Datetime
              inputProps={{
                ref: inputElement,
                onInput,
              }}
              onChange={onStartDateChanged}
              value={startDate}
              onOpen={onStartDateOpen}
              isValidDate={validateStartDate}
              className={classNames({ 'import-form-invalid': !validStartDate })}
            />
            {!validStartDate && <div className='small-medium zocks-text-danger'>The meeting date and time must be valid and before the current time.</div>}
          </div>
          <div hidden={true}>
            <label className='label-medium'>End date*</label>
            <Datetime value={endDate} onChange={onEndDateChanged} isValidDate={validateEndDate} />
          </div>
          <div className='pb-4'>
            <label htmlFor='file' className='label-medium'>
              Recording
            </label>
            <div className='d-flex'>
              <Form.Control
                ref={fileRef}
                id='file'
                type='file'
                onChange={fileUpload}
                accept='video/mp4,video/quicktime,video/webm,audio/mp4,audio/webm,audio/flac,audio/ogg,audio/mpeg,audio/x-m4a,audio/aac,audio/wav,audio/x-wav'
              />
            </div>
          </div>
          <div className='zocks-pb-1.25'>
            <UploadUserSelect selectedUsers={selectedUsers} setSelectedUsers={setSelectedUsers} />
          </div>
          {multiLanguageEnabled && hasMultiLanguageSupportQuota ? (
            <div className='pb-4'>
              <div className='d-flex gap-1'>
                <label className='label-medium'>Meeting Language</label>
                <Tooltip className='zocks-text-gray-500' placement='top' title='Zocks wil process the meeting based on the selected language option'>
                  <i className='bi bi-info-circle' />
                </Tooltip>
              </div>
              <div className='d-flex gap-2 w-100'>
                <SelectDropdown
                  className='zocks-py-0.375 zocks-px-0.75'
                  options={LANGUAGE_OPTIONS_WITH_AUTO_DETECT}
                  initialSelectedOption={LANGUAGE_OPTIONS_WITH_AUTO_DETECT.find((option) => option.id === MeetingLanguage.AUTO_DETECT)}
                  icon='bi-globe2'
                  onOptionSelected={handleOptionSelected}
                />
              </div>
            </div>
          ) : null}

          <div className='d-flex'>
            <Button variant='primary' className='w-100' onClick={onImport} disabled={fileStatus === 'loading' || !fileId || importStatus === 'loading'}>
              {fileStatus !== 'loading' && importStatus !== 'loading' && <i className='bi bi-plus-circle'></i>}
              {fileStatus === 'loading' ? 'Uploading' : 'Upload Meeting Audio'}
              {(fileStatus === 'loading' || importStatus === 'loading') && (
                <Spinner animation='border' role='status' size='sm' className='mx-2'>
                  <span className='visually-hidden'>Loading…</span>
                </Spinner>
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className='zocks-bg-color-gray-100 px-4 container-fluid phone-line-background' style={{ backgroundImage: `url(${ZocksBackgroundLogo})` }}>
        <Row className='justify-content-end py-4 phone-line-container'>
          <Col className='d-flex flex-column justify-content-between' lg={5}>
            <div>
              <div className='d-flex flex-row'>
                <div className='phone-line-icon-wrapper me-2'>
                  <PhoneLineLogo />
                </div>
                <h4 className='pb-2'> Did you know?</h4>
              </div>
              <div className='paragraph-small pb-2'>
                Your Zocks AI Assistant can connect to any Phone Solution real-time so you don't have to do post-meeting uploads manually.
              </div>
              <div className='pb-2'>
                <Button
                  variant='primaryGhost'
                  className='w-100 px-0'
                  onClick={() =>
                    (window.location.href =
                      'https://help.zocks.io/en/articles/10226330-how-do-i-use-zocks-with-a-shared-line-or-a-software-based-phone-solution')
                  }
                >
                  Learn More
                  <i className='bi bi-box-arrow-up-right'></i>
                </Button>
              </div>
              {!sharedPhoneLineEnabled && (
                <Button variant='lightPurple' className='w-100 px-0' onClick={handleSetupSharedPhoneLine}>
                  Setup Shared Phone Line
                </Button>
              )}
            </div>
            <div className='d-flex flex-column align-content-end'>
              <div className='paragraph-small'>Can’t find your phone provider, or using manual upload for a different reason?</div>
              {currentUser && (
                <div className='py-2'>
                  <Button
                    className='w-100 px-0'
                    variant='primaryGhost'
                    onClick={() => (window.location.href = `https://ljn4s9vaxf9.typeform.com/to/NTaXbj6r#email=${currentUser.email}&user_id=${currentUser.id}`)}
                  >
                    Let Us Know
                  </Button>
                </div>
              )}
              <div className='paragraph-extra-small'>Your input helps us improve Zocks and prioritize new integrations.</div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  )
}

const UploadUserSelect: React.FC<{
  selectedUsers: UserInput[]
  setSelectedUsers: (selectedUsers: UserInput[]) => void
}> = ({ selectedUsers, setSelectedUsers }) => {
  const accountId = useAppSelector(selectAccountId)
  const [userInput, setUserInput] = useState<string>('')

  const { data: users, refetch: refetchUsers } = useGetUsersQuery(
    { accountId, name: userInput, identityTypes: ['idp', 'external'], pageRequest: { page: 0, size: 500 } },
    { refetchOnMountOrArgChange: true },
  )
  const { data: currentUser } = useGetMeQuery({ accountId })
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [options, setOptions] = useState<SelectOption[]>([])
  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>([])
  const [showAddContactModal, setShowAddContactModal] = useState<boolean>(false)

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    if (!users) {
      return
    }
    const options = users.data
      .filter((u) => u.id !== currentUserId)
      .map((u) => ({
        id: u.id,
        label: getUserDisplayName(u),
      }))
    setOptions(options)
    setFilteredOptions(options)
  }, [currentUserId, users])

  const isValidUserInput = () => {
    return userInput.trim().length > 0
  }

  const addMySelf = () => {
    if (currentUser && !selectedUsers.find((user) => user.id === currentUser?.id)) {
      setSelectedUsers([
        ...selectedUsers,
        {
          type: 'managed',
          name: getUserDisplayName(currentUser),
          id: currentUser.id!,
        },
      ])
    }
  }

  useEffect(() => {
    if (currentUser) {
      addMySelf()
    }
  }, [currentUser])

  const removeUser = (removeUser: UserInput) => {
    setSelectedUsers([...selectedUsers.filter((user) => user.id !== removeUser.id)])
  }

  const addUser = () => {
    if (userInput && isValidUserInput()) {
      setSelectedUsers([...selectedUsers, { type: 'anonymous', name: userInput, id: nanoid() }])
      setUserInput('')
    }
  }

  const handleAddContactClicked = () => {
    setShowAddContactModal(true)
  }

  const handleAddContactModalSave = (newExternalUser?: UserModel) => {
    setShowAddContactModal(false)
    if (!newExternalUser) {
      return
    }
    setSelectedUsers([...selectedUsers, { id: newExternalUser.id, name: newExternalUser.name, type: 'managed' }])
    refetchUsers()
  }

  const handleAddContactModalCancel = () => {
    setShowAddContactModal(false)
  }

  const keyDownHandler = (evt: React.KeyboardEvent<HTMLElement>) => {
    if (evt.key === 'Enter') {
      evt.preventDefault()
      addUser()
    } else if (evt.key === 'Escape') {
      evt.preventDefault()
      setUserInput('')
    }
  }

  const userOptionSelectedHandler = (option: SelectOption) => {
    setSelectedUsers([...selectedUsers, { type: 'managed', name: option.label, id: option.id }])
    setUserInput('')
    setIsOpen(false)
  }

  useEffect(() => {
    setIsOpen(!!userInput)
    if (userInput) {
      const filteredOptions = options.filter((o) => o.label.toLowerCase().includes(userInput.toLowerCase()))
      setFilteredOptions(filteredOptions)
    } else {
      setFilteredOptions(options)
    }
  }, [userInput, options])

  const uploadUserDropdownContainerRef = useRef<HTMLDivElement>(null)

  useOutsideClick(uploadUserDropdownContainerRef, () => {
    setIsOpen(false)
  })

  return (
    <Row>
      <Col>
        <Row className='d-flex justify-content-between'>
          <Col className='d-flex justify-content-start'>
            <label className='label-medium'>Participants:</label>
          </Col>
          <Col className='d-flex justify-content-end'>
            <Button className='button-small zocks-text-primary' variant='link' onClick={handleAddContactClicked}>
              <>Add Contact</>
            </Button>
          </Col>
        </Row>
        <div className='d-flex gap-2'>
          <Container ref={uploadUserDropdownContainerRef}>
            <Row>
              <Col className='p-0 w-100 position-relative'>
                <div
                  className={classNames('upload-user-dropdown-container', {
                    'upload-user-dropdown-container-open': isOpen,
                  })}
                  role='button'
                  id='uploadUserDropdown'
                  onClick={toggleDropdown}
                >
                  <input
                    className={classNames('form-control border-0', {
                      invalid: isValidUserInput(),
                    })}
                    type='text'
                    list='users'
                    value={userInput}
                    placeholder='Select from your contacts'
                    onKeyDown={keyDownHandler}
                    onChange={(e) => setUserInput(e.currentTarget.value)}
                  />
                  {isOpen ? <i className='bi bi-chevron-contract'></i> : <i className='bi bi-chevron-expand'></i>}
                </div>
                <div
                  className={`overflow-auto dropdown-menu ${isOpen ? 'show' : ''} upload-user-dropdown-menu paragraph-small`}
                  aria-labelledby='uploadUserDropdown'
                >
                  {filteredOptions.map((option) => (
                    <UserTagDropdownItemOption
                      key={option.id}
                      optionId={option.id}
                      optionLabel={option.label}
                      onClick={() => userOptionSelectedHandler(option)}
                      selected={selectedUsers.some((u) => u.id === option.id)}
                    />
                  ))}
                  {options.length && !filteredOptions.length ? <div>No match. Press Enter or click the Add button to add manually.</div> : null}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
        <Stack className='my-2 align-items-start' direction='vertical' gap={3}>
          <Stack direction='horizontal' gap={2} className='flex-wrap inline-flex'>
            {selectedUsers.map((user) => {
              return (
                <TagElement key={user.id}>
                  {user.name} <CloseButton className='user-close-btn' onClick={() => removeUser(user)} />
                </TagElement>
              )
            })}
            {currentUser && !selectedUsers.find((user) => user.id === currentUser.id!) && (
              <TagElement className='zocks-bg-color-primary zocks-text-white cursor-pointer' onClick={addMySelf}>
                <i className='bi bi-plus-circle'></i> Add Myself
              </TagElement>
            )}
          </Stack>
          <AddContactModal show={showAddContactModal} onSave={handleAddContactModalSave} onCancel={handleAddContactModalCancel} />
        </Stack>
      </Col>
    </Row>
  )
}

export const TranscribeImportedSession: React.FC<{
  roomId?: string | null
  sessionId?: string | null
  onTranscribeFinished: (file: FileModel) => void
}> = ({ roomId, sessionId, onTranscribeFinished }) => {
  const { mediaClient } = useAPIClient()
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (roomId && sessionId) {
      if (timeoutId) {
        clearTimeout(timeoutId)
        setTimeoutId(null)
      }
      waitTranscriptionFinished().catch(console.error)
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        setTimeoutId(null)
      }
    }
  }, [roomId, sessionId])
  const waitTranscriptionFinished = async () => {
    if (roomId && sessionId) {
      const response = await mediaClient.listSessionFiles(roomId, sessionId)
      const transcriptionFile = response.find(isEnhancedAllocatedSessionTranscription)
      if (transcriptionFile) {
        onTranscribeFinished(transcriptionFile)
        return null
      } else {
        const timeout = setTimeout(() => {
          waitTranscriptionFinished()
          clearTimeout(timeout)
        }, 5000)
        setTimeoutId(timeout)
      }
    }
  }

  return (
    <div className='d-flex flex-column justify-content-center zocks-mt-4.5'>
      <div className='skeleton d-flex align-items-center justify-content-center' style={{ width: '100%', height: '80px' }}>
        <div className='d-flex flex-column align-items-center'>
          <div>Transcribing the recording...</div>
          <div className='small-medium'>It may take a few minutes</div>
        </div>
      </div>
    </div>
  )
}

export interface TranscriptionView {
  participantId: string
  realParticipantId?: string | null
  participantColor: string
  participantRoles: string[]
  startTime: string
  endTime: string
  transcript: string
}

export const UserTranscriptionMapping: React.FC<{
  roomSession: RoomSession | null
  transcriptionFile: FileModel | null
  onUserMappingSaved: () => void
}> = ({ roomSession, transcriptionFile, onUserMappingSaved }) => {
  const { mediaClient } = useAPIClient()

  const [transcriptions, setTranscriptions] = useState<TranscriptionView[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [saveParticipantsStatus, setSaveParticipantsStatus] = useState<'idle' | 'loading' | 'failed' | 'succeed'>('idle')
  const [hoveredParticipantId, setHoveredParticipantId] = useState<string | null>(null)
  const [transcriptionParticipantIds, setTranscriptionParticipantIds] = useState<string[]>([])
  const [transcriptionParticipantLabels, setTranscriptionParticipantLabels] = useState<{ [key: string]: string }>({})
  const [originalAllocations, setOriginalAllocations] = useState<Allocation[] | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const accountId = useAppSelector(selectAccountId)
  const [triggerEvent] = useTriggerEventMutation()

  useEffect(() => {
    if (!roomSession) {
      return
    }

    const { roomId, id: sessionId } = roomSession
    mediaClient.getUserAllocation(roomId, sessionId).then((sessionUserAllocation) => setOriginalAllocations(sessionUserAllocation.allocations))
    mediaClient.getRoomSession(roomId, sessionId).then((session) => setUsers(session.users))
  }, [roomSession, mediaClient])

  useEffect(() => {
    if (!transcriptionFile || !originalAllocations || !users.length) {
      return
    }

    mediaClient.getFileDownloadLocation(transcriptionFile.fileId).then((fileUrl) => {
      downloadTranscription(fileUrl)
    })
  }, [transcriptionFile, mediaClient, originalAllocations, users])

  const downloadTranscription = (fileUrl: FileDownloadLocationResponse) => {
    fetch(fileUrl.location, {
      method: 'GET',
    })
      .then((response) => response.blob())
      .then((blob) => {
        blob.text().then(parseTranscription)
      })
  }

  const parseTranscription = (transcriptionString: string) => {
    const transcription = JSON.parse(transcriptionString)
    const uniqueParticipants: string[] = []
    setTranscriptions(
      transcription.transcriptions.map((transcription: any) => {
        let participantIndex = uniqueParticipants.findIndex((element) => element === transcription.participantId)
        if (participantIndex < 0) {
          uniqueParticipants.push(transcription.participantId)
          participantIndex = uniqueParticipants.findIndex((element) => element === transcription.participantId)
        }
        const participantColor = chartColors[participantIndex % chartColors.length]
        return {
          participantId: transcription.participantId,
          participantColor: participantColor,
          realParticipantId: getInitialRealParticipantIdByAllocation(transcription.participantId),
          participantRoles: transcription.participantRoles,
          startTime: transcription.startTime,
          endTime: transcription.endTime,
          transcript: transcription.transcript,
        }
      }),
    )
    setTranscriptionParticipantIds(uniqueParticipants)
    setTranscriptionParticipantLabels(
      uniqueParticipants.reduce((labels: { [key: string]: string }, participant, idx) => {
        labels[participant] = `speaker_${idx}`
        return labels
      }, {}),
    )
  }

  const getInitialRealParticipantIdByAllocation = (transcriptionParticipantId: string) => {
    if (!originalAllocations?.length) {
      return users.some((u) => u.id === transcriptionParticipantId) ? transcriptionParticipantId : null
    }
    const allocation = getOriginalAllocationForTranscriptionParticipantId(transcriptionParticipantId)
    if (!allocation) {
      return null
    }
    const { userId, name } = allocation
    const user = users.find((u) => u.id === userId) ?? users.find((u) => u.name === name)
    return user?.id ?? null
  }

  const removeRealParticipant = (participantId: string) => {
    const newTranscriptions = transcriptions.map((transcription) => {
      const realParticipantId = transcription.participantId === participantId ? null : transcription.realParticipantId
      return { ...transcription, realParticipantId }
    })
    setTranscriptions(newTranscriptions)
  }

  const realParticipantIdSelected = (participantId: string, newRealParticipantId: string) => {
    if (!newRealParticipantId) {
      return
    }
    const newTranscriptions = transcriptions.map((transcription) => {
      const realParticipantId = transcription.participantId === participantId ? newRealParticipantId : transcription.realParticipantId
      const participantRoles =
        transcription.participantId === participantId
          ? users.find((user) => user.id === newRealParticipantId)?.roles.map((role) => role.id) || []
          : transcription.participantRoles
      return { ...transcription, realParticipantId, participantRoles }
    })
    setTranscriptions(newTranscriptions)
  }

  const saveParticipants = async () => {
    if (!roomSession || !transcriptionFile) {
      return
    }

    try {
      setSaveParticipantsStatus('loading')
      if (userAllocationChanged()) {
        await updateUserAllocation()
        const newTranscriptionFile = await getEnhancedSessionTranscriptionFileInfo()
        await setAllocatedOnImportMetadata(newTranscriptionFile)
      } else {
        await setAllocatedOnImportMetadata(transcriptionFile)
        await triggerSessionEvent('userAllocationAvailable')
      }
      setSaveParticipantsStatus('succeed')
      onUserMappingSaved()
    } catch (e: any) {
      if ('errors' in e) {
        setErrorMessage(e.errors.map((err: any) => err.message).join(', '))
      } else {
        setErrorMessage(JSON.stringify(e))
      }
      setSaveParticipantsStatus('failed')
    }
  }

  const userAllocationChanged = () => {
    return transcriptions.some((t) => getInitialRealParticipantIdByAllocation(t.participantId) !== t.realParticipantId)
  }

  const updateUserAllocation = async () => {
    if (!roomSession || !originalAllocations) {
      throw new Error('Unable to update user allocation without session or original allocations')
    }

    const { roomId, id: sessionId } = roomSession
    const newAllocations = []
    const replacedUserIds = new Set<string>()
    for (const originalParticipantId of transcriptionParticipantIds) {
      const user = getAllocatedUserForOriginalParticipantId(originalParticipantId)
      const originalAllocation = getOriginalAllocationForTranscriptionParticipantId(originalParticipantId)
      if (!user || !originalAllocation) {
        continue
      }
      const { transcriptDesignation } = originalAllocation
      const userId = originalAllocations.some((a) => a.userId === user.id) ? user.id : null
      const userType = user.type ?? 'anonymous'
      newAllocations.push({
        transcriptDesignation,
        userId,
        name: user.name,
        roles: user.roles.map((r) => r.id),
        email: user.email,
        userType: userType,
      })
      if (user.id !== originalParticipantId) {
        replacedUserIds.add(originalParticipantId)
      }
    }
    extendAllocationsWithMissingUsers(newAllocations, replacedUserIds)
    await mediaClient.updateUserAllocation(roomId, sessionId, { allocations: newAllocations })
  }

  const triggerSessionEvent = async (event: string) => {
    if (!roomSession) {
      throw new Error('Unable to trigger event without session')
    }
    const { roomId, id: sessionId } = roomSession
    await triggerEvent({ accountId, roomId, sessionId, body: { event } })
  }

  const getOriginalAllocationForTranscriptionParticipantId = (transcriptionParticipantId: string): Allocation | null => {
    return originalAllocations?.find((a) => a.userId === transcriptionParticipantId || a.transcriptDesignation === transcriptionParticipantId) ?? null
  }

  const getAllocatedUserForOriginalParticipantId = (originalParticipantId: string): User | null => {
    const allocatedParticipantId = transcriptions.find((t) => t.participantId === originalParticipantId)?.realParticipantId
    return users.find((u) => u.id === allocatedParticipantId) ?? null
  }

  const extendAllocationsWithMissingUsers = (allocations: Allocation[], replacedUserIds: Set<string>) => {
    for (const user of users) {
      if (user.name && !allocations.some((a) => a.userId === user.id || a.name === user.name) && !replacedUserIds.has(user.id)) {
        const userId = originalAllocations?.some((a) => a.userId === user.id) ? user.id : null
        allocations.push({
          transcriptDesignation: null,
          userId,
          name: user.name,
          roles: user.roles.map((r) => r.id),
          email: user.email,
          userType: user.type ?? 'anonymous',
        })
      }
    }
  }

  const setAllocatedOnImportMetadata = async (transcriptionFile: FileModel) => {
    const fileMetadata = JSON.parse(transcriptionFile.metadata)
    fileMetadata.allocatedOnImport = true
    await mediaClient.updateFile(transcriptionFile.fileId, { metadata: JSON.stringify(fileMetadata) })
  }

  const getEnhancedSessionTranscriptionFileInfo = async () => {
    if (!roomSession) {
      throw new Error('Unable to get session transcription without session')
    }

    const { roomId, id: sessionId } = roomSession
    const sessionFiles = await mediaClient.listSessionFiles(roomId, sessionId)
    const enhancedSessionTranscriptionFileInfo = sessionFiles.find(isEnhancedAllocatedSessionTranscription)
    if (!enhancedSessionTranscriptionFileInfo) {
      throw new Error(`No enhanced session transcription file found for roomId=${roomId}, sessionId=${sessionId}`)
    }
    return enhancedSessionTranscriptionFileInfo
  }

  const errorToast = (
    <ToastContainer className='p-3' position={'top-end'} style={{ zIndex: 1 }}>
      <Toast show={!!errorMessage} onClose={() => setErrorMessage(null)} bg='danger'>
        <Toast.Header className='ondemand-error-toast'>
          <span>Save Error</span>
        </Toast.Header>
        <Toast.Body>{'Error during importing the media: ' + errorMessage}</Toast.Body>
      </Toast>
    </ToastContainer>
  )

  const getTimeFromTimestamp = (transcriptTime: string) => {
    const startedAt = new Date(roomSession?.startedAt ?? '').getTime()
    return new Date(parseFloat(transcriptTime) * 1000 - startedAt).toISOString().slice(11, 19)
  }

  const onParticipantHover = (participantId: string) => {
    setHoveredParticipantId(participantId)
  }
  const onParticipantUnHover = () => {
    setHoveredParticipantId(null)
  }

  return (
    <div className='d-flex gap-3 flex-column overflow-hidden'>
      {errorToast}
      <div className='d-flex justify-content-between'>
        <div className='lead-medium'>Transcript</div>
        <div>
          <Button variant='primary' onClick={saveParticipants} disabled={saveParticipantsStatus === 'loading'}>
            Process Meeting
            {saveParticipantsStatus === 'loading' && (
              <Spinner animation='border' role='status' size='sm' className='mx-2'>
                <span className='visually-hidden'>Loading…</span>
              </Spinner>
            )}
          </Button>
        </div>
      </div>
      <div className='d-flex gap-3 overflow-auto justify-content-between'>
        <div className='d-flex flex-column gap-3'>
          {transcriptions.map((transcript) => {
            return (
              <div key={`${transcript.startTime}-${transcript.transcript}`}>
                <div className='paragraph-medium zocks-text-gray-600'>
                  [{getTimeFromTimestamp(transcript.startTime)}-{getTimeFromTimestamp(transcript.endTime)}]
                </div>
                <div
                  className={classNames('d-flex gap-2', {
                    'import-participant-hovered': transcript.participantId === hoveredParticipantId,
                  })}
                  onMouseEnter={() => onParticipantHover(transcript.participantId)}
                  onMouseLeave={() => onParticipantUnHover()}
                >
                  <ParticipantSelector
                    transcripts={[transcript]}
                    users={users}
                    onParticipantSelect={realParticipantIdSelected}
                    onParticipantRemove={removeRealParticipant}
                    width={'14rem'}
                    participantLabels={transcriptionParticipantLabels}
                  />
                  <div className='d-flex align-items-center paragraph-large'>{transcript.transcript}</div>
                </div>
              </div>
            )
          })}
        </div>
        <div className='d-flex flex-column gap-3 flex-shrink-0'>
          {transcriptionParticipantIds.map((participantId) => {
            const transcripts = transcriptions.filter((transcription) => transcription.participantId === participantId)
            if (!transcripts.length) {
              return <></>
            }
            return (
              <div key={`${transcripts[0].participantId}-${transcripts[0].transcript}`}>
                <ParticipantSelector
                  transcripts={transcripts}
                  users={users}
                  onParticipantSelect={realParticipantIdSelected}
                  onParticipantRemove={removeRealParticipant}
                  showDuration={true}
                  participantLabels={transcriptionParticipantLabels}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const ParticipantSelector: React.FC<{
  transcripts: TranscriptionView[]
  users: User[]
  onParticipantSelect: (participantId: string, realParticipantId: string) => void
  onParticipantRemove: (participantId: string) => void
  showDuration?: boolean
  width?: string
  participantLabels: { [key: string]: string }
}> = ({ transcripts, users, onParticipantSelect, onParticipantRemove, showDuration = false, width, participantLabels }) => {
  const getTimeFromTimestamp = (transcriptTime: number) => {
    return new Date(transcriptTime * 1000).toISOString().slice(11, 19)
  }

  const participantTime = transcripts.reduce((accumulator, transcription) => {
    return accumulator + parseFloat(transcription.endTime) - parseFloat(transcription.startTime)
  }, 0)

  const selectOptions = [{ id: '', label: 'Assign user' }].concat(
    users?.map((user) => {
      return { id: user.id, label: user.name }
    }) || [],
  )

  const originalParticipantId = transcripts[0].participantId
  const allocatedParticipantId = transcripts[0].realParticipantId

  return (
    <div
      className='import-participant-wrapper flex-column gap-1 p-1'
      style={{ backgroundColor: transcripts[0].participantColor, ...(width && { width: width }) }}
    >
      {allocatedParticipantId && (
        <div className='d-flex justify-content-between'>
          <div className='paragraph-medium'>{users.find((user) => user.id === allocatedParticipantId)?.name || allocatedParticipantId}</div>
          <span className='mx-2 cursor-pointer' onClick={() => onParticipantRemove(originalParticipantId)}>
            <i className='bi bi-x-circle'></i>
          </span>
        </div>
      )}
      {!allocatedParticipantId && (
        <div className='d-flex align-items-center gap-2 justify-content-between w-100'>
          <h6>{participantLabels[originalParticipantId] || originalParticipantId}</h6>
          <SelectDropdown
            className='py-2 zocks-px-0.75'
            options={selectOptions}
            onOptionSelected={(option) => onParticipantSelect(originalParticipantId, option.id)}
          />
        </div>
      )}
      {showDuration && <div className='paragraph-medium'>Transcription duration: {getTimeFromTimestamp(participantTime)}</div>}
    </div>
  )
}
